"use client";

import { useRouter, useParams } from "next/navigation";
import Link from "next/link";
import { type Locale, getLocaleConfig } from "@/config/locale";
import { iso2ToFlagEmoji, localeToFlagIso } from "@/lib/flag-emoji";
import { cn } from "@/lib/utils";
import { setGeoPrefCookie } from "@/lib/geo/geo-pref-client";
import { useLocaleSwitchPath } from "./locale-channel/use-locale-switch-path";

/**
 * Language switcher that swaps the locale segment in the current URL.
 * Shows language names in their native form ("English" / "Deutsch").
 *
 * `enabledLocales` is the gate's list, resolved server-side and passed down as
 * data: a client-side read would resolve the environment differently from the
 * server.
 */
export function LanguageSwitcher({
	currentLocale,
	enabledLocales,
}: {
	currentLocale: Locale;
	enabledLocales: readonly Locale[];
}) {
	const localePath = useLocaleSwitchPath(currentLocale);

	return (
		<div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
			{enabledLocales.map((locale) => {
				const config = getLocaleConfig(locale);
				const newPathname = localePath(locale);

				return (
					<Link
						key={locale}
						href={newPathname}
						className={cn(
							"text-sm transition-colors",
							locale === currentLocale
								? "font-medium text-foreground"
								: "text-muted-foreground hover:text-foreground",
						)}
						hrefLang={config.htmlLang}
					>
						{config.nativeName}
					</Link>
				);
			})}
		</div>
	);
}

/**
 * Language selector dropdown for footer use.
 * Native <select> matching the ChannelSelect pattern.
 */
export function LanguageSelect({
	currentLocale,
	enabledLocales,
	className,
	id,
	showOptionEmoji = true,
	"aria-label": ariaLabel,
	"aria-labelledby": ariaLabelledBy,
}: {
	currentLocale: Locale;
	enabledLocales: readonly Locale[];
	className?: string;
	id?: string;
	showOptionEmoji?: boolean;
	"aria-label"?: string;
	"aria-labelledby"?: string;
}) {
	const router = useRouter();
	const localePath = useLocaleSwitchPath(currentLocale);
	const params = useParams<{ locale?: string; channel?: string }>();

	return (
		<select
			id={id}
			aria-label={ariaLabel}
			aria-labelledby={ariaLabelledBy}
			className={cn(
				"h-10 w-fit rounded-md border border-input bg-background px-4 py-2 pr-10 text-sm",
				"text-foreground",
				"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
				className,
			)}
			value={currentLocale}
			onChange={(e) => {
				const newLocale = e.target.value as Locale;
				const channel = params.channel ?? "standard";
				setGeoPrefCookie({ locale: newLocale, channel });
				router.push(localePath(newLocale));
			}}
		>
			{enabledLocales.map((locale) => {
				const name = getLocaleConfig(locale).nativeName;
				return (
					<option key={locale} value={locale}>
						{showOptionEmoji ? `${iso2ToFlagEmoji(localeToFlagIso(locale))} ${name}` : name}
					</option>
				);
			})}
		</select>
	);
}

/**
 * Compact language toggle for header use.
 * Shows just the locale codes (EN / DE).
 */
export function LanguageToggle({
	currentLocale,
	enabledLocales,
}: {
	currentLocale: Locale;
	enabledLocales: readonly Locale[];
}) {
	const localePath = useLocaleSwitchPath(currentLocale);

	return (
		<div className="flex items-center gap-1 text-sm">
			{enabledLocales.map((locale, index) => {
				const newPathname = localePath(locale);

				return (
					<span key={locale} className="flex items-center">
						{index > 0 && <span className="mx-1 text-muted-foreground">/</span>}
						<Link
							href={newPathname}
							className={cn(
								"transition-colors",
								locale === currentLocale
									? "font-medium text-foreground"
									: "text-muted-foreground hover:text-foreground",
							)}
							hrefLang={getLocaleConfig(locale).htmlLang}
						>
							{locale.toUpperCase()}
						</Link>
					</span>
				);
			})}
		</div>
	);
}
